import {useRef, useState, type MouseEvent} from 'react';
import {CircleAlert, Mail, MessageSquareText, RefreshCw} from 'lucide-react';
import {useIntl} from 'react-intl';
import {PhoneHQSpinner} from 'ch3-ui-lib';
import type {FollowUpChannel} from '../../../../store/features/calls/callsService';
import {ConfirmDialog} from '../../../../components/ConfirmDialog/ConfirmDialog';
import {insightsMessages as m} from '../insightsMessages';
import {focusRing, lucide, secondaryButton, sectionTitle, tintButton} from '../insightsUi';
import {assistantErrorMessage, canRetry, isContentFiltered} from '../insightsErrors';
import {logContentCopied, logFollowUpMailAppOpened} from '../insightsLog';
import {CopyButton} from '../CopyButton';
import {smsSegments} from './smsSegments';
import {mailtoLink, MAILTO_WARN_LENGTH} from './mailto';
import type {FollowUpDrafts} from './useFollowUpDrafts';

interface FollowUpComposerProps {
    drafts: FollowUpDrafts;
    email?: string | null;
}

const softDisabled = 'aria-disabled:cursor-default aria-disabled:opacity-60';

export const FollowUpComposer = ({drafts, email}: FollowUpComposerProps) => {
    const intl = useIntl();
    const {draft, pending, error, edited, generate, update} = drafts;
    const [confirmChannel, setConfirmChannel] = useState<FollowUpChannel | null>(null);
    const confirmOrigin = useRef<HTMLElement | null>(null);
    const blocked = isContentFiltered(error?.cause);
    const busy = pending !== null;

    const request = (channel: FollowUpChannel, event?: MouseEvent<HTMLElement>) => {
        if (busy || blocked) return;
        if (edited) {
            confirmOrigin.current = event?.currentTarget ?? null;
            setConfirmChannel(channel);
        } else {
            void generate(channel);
        }
    };

    const confirm = () => {
        const channel = confirmChannel;
        setConfirmChannel(null);
        if (channel) void generate(channel);
    };

    const restoreFocus = (event: Event) => {
        const origin = confirmOrigin.current;
        if (!origin?.isConnected) return;
        event.preventDefault();
        origin.focus();
    };

    const isEmail = draft?.channel === 'EMAIL';
    const sms = draft && !isEmail ? smsSegments(draft.body) : null;
    const mailto = draft && isEmail ? mailtoLink(email, draft.subject, draft.body) : '';
    const textToCopy = () => (draft ? (isEmail && draft.subject ? `${draft.subject}\n\n${draft.body}` : draft.body) : '');

    const channelButton = (channel: FollowUpChannel) => {
        const active = draft?.channel === channel;
        const Icon = channel === 'SMS' ? MessageSquareText : Mail;
        return (
            <button
                key={channel}
                type="button"
                aria-disabled={busy || blocked || undefined}
                aria-pressed={active}
                onClick={(event) => request(channel, event)}
                className={`${active ? tintButton : secondaryButton} ${softDisabled}`}
            >
                {pending === channel ? <PhoneHQSpinner size={14}/> : <Icon size={14} aria-hidden="true" className={lucide}/>}
                {intl.formatMessage(channel === 'SMS' ? m.followUpDraftSms : m.followUpDraftEmail)}
            </button>
        );
    };

    return (
        <section aria-labelledby="insights-follow-up-title" className="flex flex-col gap-3" data-testid="insights-follow-up">
            <div className="flex flex-col gap-0.5">
                <h3 id="insights-follow-up-title" className={sectionTitle}>{intl.formatMessage(m.followUpTitle)}</h3>
                <span className="text-footnote text-ink-muted">{intl.formatMessage(m.followUpHint)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {channelButton('SMS')}
                {channelButton('EMAIL')}
                {pending && <span aria-live="polite" className="text-footnote text-ink-muted">{intl.formatMessage(m.followUpDrafting)}</span>}
            </div>
            {error && (
                <p role="alert" className="m-0 inline-flex flex-wrap items-center gap-2 text-footnote text-danger-ink">
                    <CircleAlert size={14} aria-hidden="true" className={lucide}/>
                    {assistantErrorMessage(intl, error.cause, m.followUpNeedsTranscript, m.followUpFailed)}
                    {canRetry(error.cause) && (
                        <button type="button" onClick={(event) => request(error.channel, event)} className={`cursor-pointer border-0 bg-transparent p-0 font-semibold text-brand-ink underline ${focusRing}`}>
                            {intl.formatMessage(m.tryAgain)}
                        </button>
                    )}
                </p>
            )}
            {draft && (
                <div className="flex flex-col gap-2 rounded-card border border-solid border-line p-3">
                    {isEmail && (
                        <label className="flex flex-col gap-1">
                            <span className="text-footnote text-ink-muted">{intl.formatMessage(m.followUpSubject)}</span>
                            <input
                                value={draft.subject}
                                onChange={(event) => update({subject: event.target.value})}
                                className="h-9 rounded-control border border-solid border-line bg-surface px-3 text-callout text-ink outline-none focus:border-brand-focus focus:ring-2 focus:ring-brand-focus"
                            />
                        </label>
                    )}
                    <label className="flex flex-col gap-1">
                        <span className="text-footnote text-ink-muted">{intl.formatMessage(m.followUpBody)}</span>
                        <textarea
                            value={draft.body}
                            rows={isEmail ? 8 : 4}
                            onChange={(event) => update({body: event.target.value})}
                            className="resize-y rounded-control border border-solid border-line bg-surface px-3 py-2 text-callout font-normal leading-[1.55] text-ink outline-none focus:border-brand-focus focus:ring-2 focus:ring-brand-focus"
                        />
                    </label>
                    {sms && (
                        <span data-testid="insights-sms-count" className="text-footnote tabular-nums text-ink-muted">
                            {intl.formatMessage(m.followUpSmsCount, {segments: sms.segments, length: sms.length, limit: sms.limit})}
                        </span>
                    )}
                    {isEmail && mailto.length > MAILTO_WARN_LENGTH && (
                        <p role="note" className="m-0 text-footnote text-warning-ink">{intl.formatMessage(m.followUpMailTooLong)}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <CopyButton variant="text" label={intl.formatMessage(m.followUpCopy)} getText={textToCopy} onCopied={() => logContentCopied('follow_up')}/>
                        {isEmail && (
                            <a href={mailto} onClick={logFollowUpMailAppOpened} className={secondaryButton}>
                                <Mail size={14} aria-hidden="true" className={lucide}/>
                                {intl.formatMessage(m.followUpMailApp)}
                            </a>
                        )}
                        <button
                            type="button"
                            aria-disabled={busy || undefined}
                            onClick={(event) => request(draft.channel, event)}
                            className={`${secondaryButton} ${softDisabled}`}
                        >
                            <RefreshCw size={14} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(m.followUpRegenerate)}
                        </button>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={confirmChannel !== null}
                onOpenChange={(open) => !open && setConfirmChannel(null)}
                title={intl.formatMessage(m.followUpRegenerateTitle)}
                cancelLabel={intl.formatMessage(m.cancel)}
                confirmLabel={intl.formatMessage(m.followUpRegenerateConfirm)}
                onConfirm={confirm}
                onCloseAutoFocus={restoreFocus}
            >
                {intl.formatMessage(m.followUpRegenerateBody)}
            </ConfirmDialog>
        </section>
    );
};
